import type { PropsWithChildren } from 'react';

import { cn } from '@/lib/utils';

/**
 * The shared vocabulary for a page section. Every homepage band, and the MCP connector docs, are
 * built from these — keep the scale and rhythm changes here rather than re-tuning them per section,
 * which is how the site drifted into a dozen slightly different heading sizes.
 */

/**
 * The page gutter. Exported because a couple of places need it on one axis only (`ps-`/`pe-`) and
 * so can't wrap themselves in `ContentWidth` — they must still line up with everything that does.
 */
export const GUTTER_X = 'px-5 sm:px-6 lg:px-8';

/** The same steps on the leading edge only, for elements that pad one side to the page column. */
export const GUTTER_START = 'ps-5 sm:ps-6 lg:ps-8';

/**
 * Shared max width and gutters. Also used on its own by pages that aren't a full section band, and
 * by the header/footer — everything that spans the page width goes through this, so the gutter is
 * one value rather than four copies that drift.
 */
export function ContentWidth({ children, className }: PropsWithChildren<{ className?: string }>) {
  return <div className={cn('mx-auto max-w-[1200px]', GUTTER_X, className)}>{children}</div>;
}

export function Section({
  id,
  className,
  contentClassName,
  labelledBy,
  children,
}: PropsWithChildren<{
  id?: string;
  className?: string;
  contentClassName?: string;
  labelledBy?: string;
}>) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      // scroll-mt keeps an anchored section clear of the sticky header.
      className={cn(id && 'scroll-mt-anchor', className)}
    >
      <ContentWidth className={cn('py-16 sm:py-24 lg:py-28', contentClassName)}>
        {children}
      </ContentWidth>
    </section>
  );
}

/**
 * Uppercase mono eyebrow above a section title.
 *
 * Also the section's scroll anchor: the section box opens with a screenful of top padding, so an
 * in-page link has to land on the kicker to put the heading near the top of the viewport.
 */
export function SectionKicker({ children }: { children: string }) {
  return (
    <p
      data-section-anchor=""
      className="mb-5 scroll-mt-anchor font-mono text-sm tracking-label text-faint-foreground"
    >
      {children}
    </p>
  );
}

/**
 * Ask for a size by name rather than passing `className="lg:text-[3.25rem]"` — per-section size
 * overrides are exactly how the scale drifted last time.
 *
 * `lead` is for the one or two bands that open a story and want the hero's top step. `quote` is a
 * step down, for a heading that is a whole sentence of someone's words: at the default step a
 * pull-quote runs four lines and stops reading as one.
 */
export function SectionTitle({
  id,
  as: Heading = 'h2',
  size = 'default',
  className,
  children,
}: PropsWithChildren<{
  id?: string;
  /** A page's own title is the same step as a band's; only the level differs. */
  as?: 'h1' | 'h2';
  size?: 'default' | 'lead' | 'quote';
  className?: string;
}>) {
  return (
    <Heading
      id={id}
      className={cn(
        // text-balance so a heading that wraps splits evenly instead of dropping its last word
        // onto a line of its own.
        'leading-display text-balance',
        size === 'quote' ? 'text-3xl sm:text-4xl' : 'text-4xl sm:text-5xl',
        size === 'lead' && 'lg:text-display',
        className,
      )}
    >
      {children}
    </Heading>
  );
}

/**
 * A heading whose closing clause carries the accent treatment: brand color, italic, regular weight.
 *
 * The accent is a substring of the full heading rather than a separate half, so the copy stays one
 * value in content.ts and llms.txt / metadata keep reading it as plain text. The trade: editing the
 * heading without editing its accent silently drops the highlight instead of breaking the build.
 */
export function AccentedTitle({ text, accent }: { text: string; accent: string }) {
  const at = text.lastIndexOf(accent);
  if (at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <span className="font-normal text-brand italic">{accent}</span>
      {text.slice(at + accent.length)}
    </>
  );
}

/** The lead paragraph under a section title. */
export function SectionLead({ className, children }: PropsWithChildren<{ className?: string }>) {
  return (
    <p className={cn('text-lg leading-relaxed text-muted-foreground', className)}>{children}</p>
  );
}
